import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { STAGE_LABELS, SOURCE_LABELS, TARGET_LABELS } from "../lib/constants";
import { LevelBadge } from "../components/LevelBadge";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, Loader2, Rocket, FileCheck2, Hammer } from "lucide-react";

const DELIVERED_STAGES = ["preventivo", "contratto"];

function ProjectTable({ rows, navigate, testid }) {
  return (
    <Card className="rounded border-border overflow-x-auto" data-testid={testid}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-border">
            <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Commessa</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Target</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Origine</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Fase</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Assegnato</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-right">Punteggio</TableHead>
            <TableHead className="text-xs uppercase tracking-wider">Livello</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-right">Impianto</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-right">Accumulo</TableHead>
            <TableHead className="text-xs uppercase tracking-wider text-right">Valore</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((l) => (
            <TableRow key={l.id} onClick={() => navigate(`/leads/${l.id}`)} data-testid={`project-row-${l.id}`} className="cursor-pointer border-border">
              <TableCell className="font-medium">{l.name}</TableCell>
              <TableCell className="text-sm font-mono">{l.contract?.commessa || "—"}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{TARGET_LABELS[l.target]}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{SOURCE_LABELS[l.source]}</TableCell>
              <TableCell className="text-sm">{STAGE_LABELS[l.stage]}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{l.assigned_to_name || "—"}</TableCell>
              <TableCell className="text-right font-heading font-bold">{l.score ?? "—"}</TableCell>
              <TableCell><LevelBadge level={l.level} /></TableCell>
              <TableCell className="text-right font-heading">{l.analysis?.pv_kwp ? `${l.analysis.pv_kwp} kWp` : "—"}</TableCell>
              <TableCell className="text-right font-heading">{l.analysis?.storage_kwh ? `${l.analysis.storage_kwh} kWh` : "—"}</TableCell>
              <TableCell className="text-right font-heading">{l.cost_estimate?.prezzo ? Number(l.cost_estimate.prezzo).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function Projects() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/leads", { params: { level: 3, q: q || undefined } }).then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  // Il backend esclude già i lead al cimitero (cemetery != true).
  const inCorso = (leads || []).filter((l) => !DELIVERED_STAGES.includes(l.stage));
  const consegnati = (leads || []).filter((l) => DELIVERED_STAGES.includes(l.stage));

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded bg-primary/10 flex items-center justify-center"><Rocket className="h-4 w-4 text-primary" strokeWidth={1.5} /></div>
          <div>
            <h1 className="text-xl font-heading font-bold">Progetti</h1>
            <p className="text-xs text-muted-foreground">Lead prioritari (Livello 3) per cui sviluppare un progetto dedicato</p>
          </div>
        </div>
        {leads && <span className="text-sm font-heading font-bold text-primary" data-testid="projects-count">{leads.length} progetti</span>}
      </header>

      <div className="p-4 lg:p-8 space-y-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome..." className="pl-9" data-testid="projects-search" />
        </div>

        {!leads ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : leads.length === 0 ? (
          <Card className="rounded border-border"><p className="p-10 text-center text-sm text-muted-foreground">Nessun progetto: profila i lead come Livello 3 per classificarli come prioritari.</p></Card>
        ) : (
          <>
            {/* Blocco 1 — Progetti in essere */}
            <section className="space-y-3" data-testid="projects-block-incorso">
              <div className="flex items-center gap-2">
                <Hammer className="h-4 w-4 text-primary" strokeWidth={1.5} />
                <h2 className="font-heading font-bold text-lg">Progetti in essere</h2>
                <span className="text-xs font-heading font-bold text-primary" data-testid="incorso-count">{inCorso.length}</span>
                <span className="text-xs text-muted-foreground">· da profilazione a consulenza tecnica</span>
              </div>
              {inCorso.length === 0 ? (
                <Card className="rounded border-border border-dashed"><p className="p-6 text-center text-sm text-muted-foreground">Nessun progetto in essere.</p></Card>
              ) : <ProjectTable rows={inCorso} navigate={navigate} testid="projects-table-incorso" />}
            </section>

            {/* Blocco 2 — Preventivo consegnato o contratto */}
            <section className="space-y-3" data-testid="projects-block-consegnati">
              <div className="flex items-center gap-2">
                <FileCheck2 className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                <h2 className="font-heading font-bold text-lg">Preventivo consegnato / Contratto</h2>
                <span className="text-xs font-heading font-bold text-emerald-500" data-testid="consegnati-count">{consegnati.length}</span>
              </div>
              {consegnati.length === 0 ? (
                <Card className="rounded border-border border-dashed"><p className="p-6 text-center text-sm text-muted-foreground">Nessun progetto con preventivo consegnato o contratto.</p></Card>
              ) : <ProjectTable rows={consegnati} navigate={navigate} testid="projects-table-consegnati" />}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
